// MobX
import { makeAutoObservable } from 'mobx';

// APIs
import { resetUnreadMessages } from '@/apis/chatApi';

// Stores
import { chatStateStore } from '../ChatStateStore';
import { chatUserPickerStore } from '../ChatUserPickerStore';
import { chatCredentialsStore } from '../ChatCredentialsStore';

// Services
import { MessageSender } from './MessageSender';
import { MessageProcessor } from './MessageProcessor';
import { IframeStateManager } from './IframeStateManager';
import { loginStateManager } from './LoginStateManager';
import { IframeMessageSender } from './IframeMessageSender';
import { IframeEventDispatcher } from './IframeEventDispatcher';
import { IframeErrorHandler } from './IframeErrorHandler';
import { IframeMessageHandler } from './IframeMessageHandler';
import { IframeScrollManager } from './IframeScrollManager';

// Constants
import { EVENT_TYPES, ERROR_MESSAGES } from './MessageTypes';

/**
 * Iframe通信存储
 * 遵循SOLID原则，负责管理父窗体和iframe之间的通信
 */
class IframeCommunicationStore {
  constructor() {
    makeAutoObservable(this);

    // 初始化各个组件
    this.messageSender = new MessageSender();
    this.messageProcessor = new MessageProcessor();
    this.stateManager = new IframeStateManager();
    this.iframeMessageSender = new IframeMessageSender();
    this.eventDispatcher = new IframeEventDispatcher();
    this.errorHandler = new IframeErrorHandler();
    this.scrollManager = new IframeScrollManager(this.iframeMessageSender);

    // 绑定消息处理回调
    this._bindMessageHandlerCallbacks();

    // 初始化消息处理器
    this.messageHandler = new IframeMessageHandler(
      this.eventDispatcher,
      this.stateManager,
      chatStateStore,
      null, // 已删除 messageStatusStore
      this.messageHandlerCallbacks,
      this.scrollManager
    );

    // 设置iframe挂载回调
    this.messageHandler.onMountedCallback = () => {
      const iframe = document.querySelector('iframe');
      if (iframe) {
        this.sendInitMessage(iframe);
      }
    };
  }

  async _resetUnreadMessagesDirectly(conversationId) {
    if (!conversationId) return;

    chatUserPickerStore.clearUnreadMessages();

    const selectedUserId = chatUserPickerStore.selectedUserId;
    if (selectedUserId) {
      chatUserPickerStore.updateUserUnreadCount(selectedUserId, 0);

      // 调用后端 API 重置未读消息
      resetUnreadMessages(conversationId, { showLoading: false }).catch(error => {
        // 静默处理错误，不影响用户体验
        console.warn('[IframeCommunicationStore] 重置未读消息失败:', error);
      });
    }
  }

  /**
   * 绑定消息处理器的回调函数
   */
  _bindMessageHandlerCallbacks() {
    this.messageHandlerCallbacks = {
      onInitSuccess: async data => {
        this.stateManager.setInitialized(true);
        chatStateStore.setError(null);
        chatStateStore.setUpdating(false);

        // 获取当前会话ID并直接重置未读消息
        if (chatUserPickerStore?.conversationId) {
          await this._resetUnreadMessagesDirectly(chatUserPickerStore.conversationId);
        }

        // 触发自定义事件
        this.eventDispatcher.dispatchChatLoginSuccess(data);
      },

      onInitError: error => {
        chatStateStore.setError(error);
        chatStateStore.setUpdating(false);

        // 触发自定义事件
        this.eventDispatcher.dispatchChatLoginError(error);
      },

      onLogoutSuccess: data => {
        // 处理注销成功，更新登录状态管理器
        loginStateManager.handleLogout(data.reason);

        this.eventDispatcher.dispatchChatLogoutSuccess(data.reason);
      },

      onLogoutError: data => {
        this.eventDispatcher.dispatchChatLogoutError(data.error, data.reason);
      },

      onSwitchConversationSuccess: async data => {
        this.stateManager.setSwitchingConversation(false);
        chatStateStore.setUpdating(false);
        chatStateStore.setConnecting(false);
        chatStateStore.setPreparing(false);
        chatStateStore.setInitializing(false);

        // 切换会话成功后直接重置未读消息
        if (data.conversationId) {
          await this._resetUnreadMessagesDirectly(data.conversationId);
        }

        // 通知其他组件切换会话成功
        this.eventDispatcher.dispatchConversationSwitched(data.conversationId, data.message);

        // 注意：滚动操作已移到IframeMessageHandler中执行
      },

      onSwitchConversationError: data => {
        this.stateManager.setSwitchingConversation(false);
        chatStateStore.setUpdating(false);
        chatStateStore.setConnecting(false);
        chatStateStore.setPreparing(false);
        chatStateStore.setInitializing(false);

        // 处理错误
        const errorInfo = this.errorHandler.handleSwitchConversationError(data.error);

        // 显示错误信息
        chatStateStore.setError(data.error || '切换会话失败');
        if (errorInfo.errorType === 'warning') {
          chatStateStore.showWarning(errorInfo.errorMessage);
        } else {
          chatStateStore.showError(errorInfo.errorMessage);
        }

        // 触发错误事件
        this.eventDispatcher.dispatchConversationSwitchError(
          data.conversationId,
          data.error,
          errorInfo.errorMessage,
          errorInfo.errorType
        );
      },

      onNotifyComplete: () => {
        // 处理通知完成事件
      },
    };
  }

  // Getter 方法，委托给状态管理器
  get isInitialized() {
    return this.stateManager.isInitialized;
  }

  get isIframeReady() {
    return this.stateManager.isIframeReady;
  }

  get error() {
    return this.stateManager.error;
  }

  // 状态设置方法，委托给状态管理器
  setInitialized = value => {
    this.stateManager.setInitialized(value);
  };

  setIframeReady = value => {
    this.stateManager.setIframeReady(value);
  };

  setError = error => {
    this.stateManager.setError(error);
  };

  reset = () => {
    this.stateManager.reset();
    this.messageProcessor.cleanup();
    loginStateManager.reset();
    chatStateStore.reset();
  };

  sendInitMessage = async iframe => {
    const credentials = chatCredentialsStore.credentials;

    const validation = this.errorHandler.validateInitParams({
      iframe,
      credentials,
      conversationId: chatUserPickerStore?.conversationId,
    });

    if (!validation.isValid) {
      validation.errors.forEach(error => {
        this.setError(error);
      });
      return;
    }

    chatStateStore.setConnecting(true);
    chatStateStore.setError(null);

    const groupOwnerId = credentials?.conversation?.creator?.toString();
    if (!groupOwnerId) {
      const errorMsg = 'API 未返回群主ID，聊天初始化失败';
      chatStateStore.setError(errorMsg);
      chatStateStore.showError('聊天初始化失败：API 数据不完整，请联系管理员');
      return;
    }

    this.iframeMessageSender.sendInitMessage(
      iframe,
      credentials,
      chatUserPickerStore.conversationId,
      groupOwnerId
    );

    setTimeout(() => {
      if (!this.isInitialized) {
        // 初始化消息发送后未收到响应，可能iframe未完全加载
      }
    }, 3000);

    chatStateStore.setConnecting(false);
  };

  sendLogoutMessage = async (iframe, reason = 'manual') => {
    if (!iframe) {
      this.setError(ERROR_MESSAGES.IFRAME_NOT_EXISTS);
      return;
    }

    this.iframeMessageSender.sendLogoutMessage(iframe, reason);
  };

  sendSwitchConversationMessage = async (
    iframe,
    conversationId,
    serviceUserId,
    credentials = null
  ) => {
    const groupOwnerId = credentials?.conversation?.creator?.toString();

    if (!groupOwnerId) {
      const errorMsg = 'API 未返回群主ID，切换会话失败';
      chatStateStore.setError(errorMsg);
      chatStateStore.showError('切换会话失败：API 数据不完整，请联系管理员');

      chatStateStore.setUpdating(false);
      chatStateStore.setSwitchingConversation(false);
      chatStateStore.setConnecting(false);
      chatStateStore.setPreparing(false);
      chatStateStore.setInitializing(false);
      chatStateStore.clearNotification();

      return Promise.reject(new Error(errorMsg));
    }

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        // 切换会话超时
        chatStateStore.setSwitchingConversation(false);
        chatStateStore.setUpdating(false);
        chatStateStore.setConnecting(false);
        chatStateStore.setPreparing(false);
        chatStateStore.setInitializing(false);
        reject(new Error(ERROR_MESSAGES.SWITCH_CONVERSATION_TIMEOUT));
      }, 10000);

      const handleSwitchSuccess = event => {
        if (event.type === EVENT_TYPES.CONVERSATION_SWITCHED) {
          clearTimeout(timeout);
          window.removeEventListener(EVENT_TYPES.CONVERSATION_SWITCHED, handleSwitchSuccess);
          window.removeEventListener(EVENT_TYPES.CONVERSATION_SWITCH_ERROR, handleSwitchError);
          resolve();
        }
      };

      const handleSwitchError = event => {
        clearTimeout(timeout);
        window.removeEventListener(EVENT_TYPES.CONVERSATION_SWITCHED, handleSwitchSuccess);
        window.removeEventListener(EVENT_TYPES.CONVERSATION_SWITCH_ERROR, handleSwitchError);
        reject(new Error(event.detail.error || '切换会话失败'));
      };

      window.addEventListener(EVENT_TYPES.CONVERSATION_SWITCHED, handleSwitchSuccess);
      window.addEventListener(EVENT_TYPES.CONVERSATION_SWITCH_ERROR, handleSwitchError);

      chatStateStore.setConnecting(true);
      chatStateStore.setError(null);
      chatStateStore.setUpdating(true);
      chatStateStore.setSwitchingConversation(true);

      this.iframeMessageSender.sendSwitchConversationMessage(
        iframe,
        conversationId,
        serviceUserId,
        credentials,
        groupOwnerId
      );

      chatStateStore.setConnecting(false);
    });
  };

  // 处理来自 iframe 的消息
  handleMessage = event => {
    const iframe = document.querySelector('iframe');
    this.messageHandler.handleMessage(event, iframe);
  };

  /**
   * 等待登录成功
   */
  waitForLoginSuccess() {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error(ERROR_MESSAGES.LOGIN_TIMEOUT));
      }, 10000); // 10秒超时

      const handleLoginSuccess = event => {
        if (event.type === EVENT_TYPES.CHAT_LOGIN_SUCCESS) {
          clearTimeout(timeout);
          window.removeEventListener(EVENT_TYPES.CHAT_LOGIN_SUCCESS, handleLoginSuccess);
          window.removeEventListener(EVENT_TYPES.CHAT_LOGIN_ERROR, handleLoginError);
          resolve();
        }
      };

      const handleLoginError = event => {
        clearTimeout(timeout);
        window.removeEventListener(EVENT_TYPES.CHAT_LOGIN_SUCCESS, handleLoginSuccess);
        window.removeEventListener(EVENT_TYPES.CHAT_LOGIN_ERROR, handleLoginError);
        reject(new Error(event.detail.error || ERROR_MESSAGES.LOGIN_FAILED));
      };

      window.addEventListener(EVENT_TYPES.CHAT_LOGIN_SUCCESS, handleLoginSuccess);
      window.addEventListener(EVENT_TYPES.CHAT_LOGIN_ERROR, handleLoginError);
    });
  }

  /**
   * 等待注销成功
   */
  _waitForLogoutSuccess() {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error(ERROR_MESSAGES.LOGOUT_TIMEOUT));
      }, 5000); // 5秒超时

      const handleLogoutSuccess = event => {
        if (event.type === EVENT_TYPES.CHAT_LOGOUT_SUCCESS) {
          clearTimeout(timeout);
          window.removeEventListener(EVENT_TYPES.CHAT_LOGOUT_SUCCESS, handleLogoutSuccess);
          window.removeEventListener(EVENT_TYPES.CHAT_LOGOUT_ERROR, handleLogoutError);
          resolve();
        }
      };

      const handleLogoutError = event => {
        clearTimeout(timeout);
        window.removeEventListener(EVENT_TYPES.CHAT_LOGOUT_SUCCESS, handleLogoutSuccess);
        window.removeEventListener(EVENT_TYPES.CHAT_LOGOUT_ERROR, handleLogoutError);
        reject(new Error(event.detail.error || ERROR_MESSAGES.LOGOUT_FAILED));
      };

      window.addEventListener(EVENT_TYPES.CHAT_LOGOUT_SUCCESS, handleLogoutSuccess);
      window.addEventListener(EVENT_TYPES.CHAT_LOGOUT_ERROR, handleLogoutError);
    });
  }

  // 移除子窗体增量更新通知处理方法
  // 不再需要子窗体通知来触发增量更新
}

export const iframeCommunicationStore = new IframeCommunicationStore();
